import { useId, useRef } from 'react'
import { SelectProps } from './props.js'
import { ChevronDownIcon } from './icons.js'

export function Select({
  label,
  value,
  onChange,
  options,
  placeholder,
  error,
  supportingText,
  disabled = false,
  fullWidth = false,
  className = '',
}: SelectProps) {
  const id = useId()
  const supportingId = useId()
  const selectRef = useRef<HTMLSelectElement>(null)

  const isError = !!error

  const containerClasses = [
    'flex flex-col gap-0',
    fullWidth && 'w-full',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const selectWrapperClasses = [
    'relative flex items-center',
    'bg-surface-low border border-outline-variant rounded-md',
    'interactive',
    isError && 'border-error',
    disabled && 'opacity-38',
  ]
    .filter(Boolean)
    .join(' ')

  const selectClasses = [
    'flex-1 appearance-none bg-transparent outline-none px-3 py-2 h-9',
    'text-body-md text-on-surface',
    'pe-10',
  ]
    .filter(Boolean)
    .join(' ')

  const labelClasses = 'text-label-md text-on-surface-variant mb-1.5 block'

  const supportingClasses = [
    'text-body-sm mt-1.5',
    isError ? 'text-error' : 'text-on-surface-variant',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={containerClasses}>
      <label htmlFor={id} className={labelClasses}>
        {label}
      </label>
      <div className={selectWrapperClasses}>
        <select
          ref={selectRef}
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          aria-invalid={isError}
          aria-describedby={isError ? supportingId : undefined}
          className={selectClasses}
        >
          {placeholder && (
            <option value="">{placeholder}</option>
          )}
          {options.map((opt) => (
            <option
              key={opt.value}
              value={opt.value}
              style={{
                backgroundColor: 'var(--c-surface-container)',
                color: 'var(--c-on-surface)',
              }}
            >
              {opt.label}
            </option>
          ))}
        </select>
        <div className="pointer-events-none absolute end-3 flex items-center text-on-surface-variant">
          <ChevronDownIcon className="w-4 h-4" />
        </div>
      </div>
      {(error || supportingText) && (
        <p
          id={supportingId}
          className={supportingClasses}
        >
          {error || supportingText}
        </p>
      )}
    </div>
  )
}
